import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { Col, FormLabel, Row } from "react-bootstrap";

import ListPage from "../../../layouts/page/ListPage";
import PageTitle from "../../../layouts/PageTitle";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import ListMetricCard, { ListMetricCards } from "../../../components/Common/ListParts/ListMetricCard";
import { ApiError } from "../../../components/Common/ApiError";
import { Columns } from "./Columns";
import { currentMonth, monthToApi } from "./bonusMonth";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";

const EmployeeBonusList = () => {

    const { t } = useTranslation();
    // A tela abre nas bonificações vigentes no mês corrente: é o que entra no acerto deste mês.
    // Sem mês, a grade traria encerradas de anos atrás misturadas com as que valem hoje.
    const [monthFilter, setMonthFilter] = useState(currentMonth);
    const [employeeFilter, setEmployeeFilter] = useState(null);

    /** Traduz os campos da tela para os filtros que a API entende. */
    const buildFilters = useCallback((month, employee) => {
        const activeFilters = [];

        // "Vigente no mês" não é um campo: o backend traduz em começou até o mês e ainda não
        // terminou (ver EmployeeBonusSpecification).
        if (month) {
            activeFilters.push({
                field: "activeMonth",
                value: monthToApi(month),
                operation: SearchOperation.EQUAL
            });
        }

        if (employee) {
            activeFilters.push({
                field: "employee.id",
                value: employee.value,
                operation: SearchOperation.EQUAL
            });
        }

        return activeFilters;
    }, []);

    const [filters, setFilters] = useState(() => buildFilters(currentMonth(), null));

    const [totalValue, setTotalValue] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [totalEmployees, setTotalEmployees] = useState(0);
    const [loadingTotals, setLoadingTotals] = useState(false);

    // Os cards somam os mesmos filtros da grade numa consulta própria: o ListPage pagina e não
    // devolve os registros para a tela, então somar a página daria um total menor que o real.
    useEffect(() => {
        setLoadingTotals(true);
        axiosInstance.post(`${Api.cash_flow.employee_bonus}/findByFilters`, { filters })
            .then(({ data }) => {
                const rows = data || [];
                // Com o filtro de mês, cada linha vale uma vez naquele mês: a soma é o que a
                // casa paga de bonificação no mês.
                setTotalValue(rows.reduce((total, item) => total + Number(item.value || 0), 0));
                setTotalCount(rows.length);
                setTotalEmployees(new Set(rows.map(item => item.employee?.id).filter(Boolean)).size);
            })
            .catch(error => ApiError(error))
            .finally(() => setLoadingTotals(false));
    }, [filters]);

    const handleSearch = () => {
        setFilters(buildFilters(monthFilter, employeeFilter));
    };

    // Limpar volta para o mês corrente, e não para o histórico inteiro.
    const handleClearFilters = () => {
        const month = currentMonth();
        setMonthFilter(month);
        setEmployeeFilter(null);
        setFilters(buildFilters(month, null));
    };

    return (
        <>
            <PageTitle activeMenu={t('cash_flow.employee_bonuses')} motherMenu={t('cash_flow.label')} />

            <FilterAccordion
                title="filters"
                onSearch={handleSearch}
                onClear={handleClearFilters}
                centerButtons={false}
            >
                {/* O mês vem primeiro, como o período nas demais listagens. Colunas md para os
                    filtros empilharem no celular. */}
                <Row className="g-2 align-items-end">
                    <Col lg={3} md={6}>
                        <FormLabel>{t('cash_flow.employee_bonus_active_month')}</FormLabel>
                        <InputDate
                            monthOnly
                            selected={monthFilter}
                            onChange={setMonthFilter}
                        />
                    </Col>
                    <Col lg={4} md={6}>
                        <FormLabel>{t('employee')}</FormLabel>
                        <EmployeeSelect
                            filterProvidesService={false}
                            value={employeeFilter}
                            onChange={setEmployeeFilter}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <ListMetricCards>
                <ListMetricCard
                    accent="primary"
                    label={t('cash_flow.employee_bonus_total_in_month')}
                    value={`${t('currency_symbol')} ${convertDoubleToStringPTBR(totalValue)}`}
                    icon="fas fa-gift"
                    loading={loadingTotals}
                />
                <ListMetricCard
                    accent="warning"
                    label={t('cash_flow.employee_bonus_count')}
                    value={totalCount}
                    icon="fas fa-receipt"
                    loading={loadingTotals}
                />
                <ListMetricCard
                    accent="info"
                    label={t('cash_flow.employee_bonus_employees')}
                    value={totalEmployees}
                    icon="fas fa-users"
                    loading={loadingTotals}
                />
            </ListMetricCards>

            {/* Sem o item "Inativos": bonificação lançada errado é excluída, e a que parou de
                valer é encerrada pelo mês final, então não existe bonificação inativa. */}
            <ListPage
                showPageTitle={false}
                activeMenu={t('cash_flow.employee_bonuses')}
                motherMenu={t('cash_flow.label')}
                path={Api.cash_flow.employee_bonus}
                route={MenuConfig.cash_flow.employee_bonus.route}
                columns={Columns}
                filters={filters}
                hideInactiveToggle
            />
        </>
    );
}

export default EmployeeBonusList;
